import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Container, Table } from 'reactstrap';
import DateFilterTabs from 'components/Dashboard/components/DateFilterTabs';
import StatCard from 'components/Dashboard/components/StatCard';
import ChartCard from 'components/Dashboard/components/ChartCard';
import InventoryInsightCard from 'components/Dashboard/components/InventoryInsightCard';
import ActivityFeed from 'components/Dashboard/components/ActivityFeed';
import DashboardWidget from 'components/Dashboard/components/DashboardWidget';
import RevenueChart from 'components/Dashboard/charts/RevenueChart';
import BarChart from 'components/Dashboard/charts/BarChart';
import SalesUptimeChart from 'components/Dashboard/charts/SalesUptimeChart';
import Can from 'components/Common/Can';
import { useShop } from 'context/shopContext';
import { usePermissions } from 'context/PermissionProvider';
import { PERMISSIONS } from 'constants/permissions';
import { SHOP_PATHS } from 'routes/routesConstants';
import {
  useInvalidateShopDashboard,
  useShopDashboardActivities,
  useShopDashboardCharts,
  useShopDashboardInventory,
  useShopDashboardMetrics,
  useShopDashboardRecentLedgers,
  useShopDashboardRecentSellAdjustments,
  useShopDashboardRecentSells,
  useShopDashboardRecentStock,
  useShopDashboardSalesUptime,
} from 'http-client/hooks/useShopDashboardWidgets';
import type { ActivityLogDto } from 'http-client/types/dashboard.types';
import { formatPkr, formatCount } from 'utils/currency';
import { fmtDate } from 'utils/common';
import {
  DASHBOARD_INSIGHT_TO_FILTER,
  filtersToSearchParams,
} from 'pages/Tenant/Inventory/inventoryFilters';
import './SellDashboardPage.css';

type DateRangeKey = 'today' | 'week' | 'month' | 'year' | 'custom';

const DEFAULT_LOGO = 'https://placehold.co/96x96?text=Shop';

const formatRelativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const mapActivitiesForFeed = (items: ActivityLogDto[]) =>
  items.map((item) => {
    const type = item.type as 'sale' | 'purchase' | 'return' | 'stock';
    const config: Record<string, { icon: string; color: string }> = {
      sale: { icon: 'bx-shopping-bag', color: '#0284c7' },
      purchase: { icon: 'bx-cart', color: '#06b6d4' },
      return: { icon: 'bx-undo', color: '#ef4444' },
      stock: { icon: 'bx-package', color: '#f59e0b' },
    };
    const meta = config[type] ?? { icon: 'bx-info-circle', color: '#6b7280' };
    return {
      id: item.id,
      type,
      description: item.description,
      amount: item.amount ? formatPkr(item.amount) : undefined,
      timestamp: formatRelativeTime(item.createdAt),
      icon: meta.icon,
      color: meta.color,
    };
  });

const SellDashboardPage: React.FC = () => {
  const { shopId = '' } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const { shop } = useShop();
  const { hasAnyPermission } = usePermissions();
  const canWidget = (widgetPermission: string) =>
    hasAnyPermission([widgetPermission, PERMISSIONS.VIEW_DASHBOARD]);
  const invalidateDashboard = useInvalidateShopDashboard();

  const [dateRange, setDateRange] = useState<DateRangeKey>('today');
  const [customDateRange, setCustomDateRange] = useState<{ start?: Date; end?: Date }>({});

  const canMetrics = canWidget(PERMISSIONS.DASHBOARD_WIDGET_METRICS);
  const canCharts = canWidget(PERMISSIONS.DASHBOARD_WIDGET_CHARTS);
  const canUptime = canWidget(PERMISSIONS.DASHBOARD_WIDGET_SALES_UPTIME);
  const canInventory = canWidget(PERMISSIONS.DASHBOARD_WIDGET_INVENTORY);
  const canActivities = canWidget(PERMISSIONS.DASHBOARD_WIDGET_ACTIVITIES);
  const canRecentSells = canWidget(PERMISSIONS.DASHBOARD_WIDGET_RECENT_SALES);
  const canRecentAdj = canWidget(PERMISSIONS.DASHBOARD_WIDGET_RECENT_SELL_ADJUSTMENTS);
  const canRecentStock = canWidget(PERMISSIONS.DASHBOARD_WIDGET_RECENT_STOCK);
  const canRecentLedgers = canWidget(PERMISSIONS.DASHBOARD_WIDGET_RECENT_LEDGERS);

  const metricsQuery = useShopDashboardMetrics(
    shopId,
    dateRange,
    customDateRange.start,
    customDateRange.end,
    canMetrics,
  );
  const chartsQuery = useShopDashboardCharts(
    shopId,
    dateRange,
    customDateRange.start,
    customDateRange.end,
    canCharts,
  );
  const uptimeQuery = useShopDashboardSalesUptime(
    shopId,
    dateRange,
    customDateRange.start,
    customDateRange.end,
    canUptime,
  );
  const inventoryQuery = useShopDashboardInventory(shopId, canInventory);
  const activitiesQuery = useShopDashboardActivities(shopId, 15, canActivities);
  const recentSellsQuery = useShopDashboardRecentSells(shopId, canRecentSells);
  const recentAdjQuery = useShopDashboardRecentSellAdjustments(shopId, canRecentAdj);
  const recentStockQuery = useShopDashboardRecentStock(shopId, canRecentStock);
  const recentLedgersQuery = useShopDashboardRecentLedgers(shopId, canRecentLedgers);

  const stats = metricsQuery.data?.stats;
  const trends = metricsQuery.data?.trends;

  const formatTrend = (value?: number) => {
    if (value === undefined || value === null) return undefined;
    return { value: Math.abs(value), isPositive: value >= 0 };
  };

  const handleFilterChange = (filter: string, range?: { from: string; to: string }) => {
    setDateRange(filter as DateRangeKey);
    if (range?.from && range?.to) {
      setCustomDateRange({ start: new Date(range.from), end: new Date(range.to) });
    }
  };

  const handleRefreshAll = () => invalidateDashboard(shopId);

  const revenueChartData = useMemo(
    () =>
      (chartsQuery.data?.revenueChart?.series ?? []).map((p) => ({
        month: p.label,
        revenue: p.value2,
        sales: p.value1,
      })),
    [chartsQuery.data],
  );

  const topItemsData = useMemo(
    () =>
      (chartsQuery.data?.topItems?.items ?? []).map((i) => ({
        name: i.name,
        value: i.quantity,
      })),
    [chartsQuery.data],
  );

  const uptimeData = useMemo(
    () =>
      (uptimeQuery.data?.series ?? []).map((p) => ({
        label: p.label,
        invoiceCount: p.value1,
        revenue: p.value2,
      })),
    [uptimeQuery.data],
  );

  const feedActivities = useMemo(
    () => mapActivitiesForFeed(activitiesQuery.data ?? []),
    [activitiesQuery.data],
  );

  const paths = useMemo(
    () => ({
      sells: SHOP_PATHS.MEDICINE_SELL_INVOICES.replace(':shopId', shopId),
      sellAdj: SHOP_PATHS.SELL_ADJUSTMENT_ITEMS.replace(':shopId', shopId),
      stock: SHOP_PATHS.STOCK_MOVEMENTS.replace(':shopId', shopId),
      ledger: SHOP_PATHS.LEDGER_ENTRIES.replace(':shopId', shopId),
      inventories: SHOP_PATHS.INVENTORIES.replace(':shopId', shopId),
    }),
    [shopId],
  );

  const navigateToInventoryWithFilters = (insightCategory?: string) => {
    const base = paths.inventories;
    if (!insightCategory) {
      navigate(base);
      return;
    }
    const preset = DASHBOARD_INSIGHT_TO_FILTER[insightCategory];
    if (!preset) {
      navigate(base);
      return;
    }
    const qs = filtersToSearchParams(preset).toString();
    navigate(qs ? `${base}?${qs}` : base);
  };

  const insightConfig: Record<
    string,
    { icon: string; severity: 'info' | 'warning' | 'critical'; title: string }
  > = {
    dead: { icon: 'bx-trash', severity: 'critical', title: 'Dead stock (never sold)' },
    belowShelf: { icon: 'bx-down-arrow-alt', severity: 'warning', title: 'Below shelf level' },
    nearExpiry: { icon: 'bx-time-five', severity: 'critical', title: 'Near expiry (30 days)' },
  };

  return (
    <div className="page-content sell-dashboard-page">
      <Container fluid>
        <div className="dashboard-container">
          <header className="shop-dashboard-hero">
            <div className="shop-dashboard-hero-brand">
              <img
                src={shop.logoUrl?.trim() || DEFAULT_LOGO}
                alt={shop.name}
                className="shop-dashboard-logo"
                onError={(e) => {
                  e.currentTarget.src = DEFAULT_LOGO;
                }}
              />
              <div>
                <h1 className="shop-dashboard-title">{shop.name}</h1>
                <p className="shop-dashboard-subtitle">
                  {shop.city}, {shop.state} — overview of what is happening in your shop
                </p>
              </div>
            </div>
            <Button
              color="primary"
              outline
              onClick={handleRefreshAll}
              className="dashboard-refresh-all"
            >
              <i className="bx bx-revision me-1" /> Refresh dashboard
            </Button>
          </header>

          <DateFilterTabs onFilterChange={handleFilterChange} activeFilter={dateRange} />

          <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_METRICS, PERMISSIONS.VIEW_DASHBOARD]}>
            <motion.section
              className="stats-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <StatCard
                title="Pieces sold"
                value={stats?.totalItemsSold ?? 0}
                icon="bx-shopping-bag"
                trend={formatTrend(trends?.sales)}
                gradient="linear-gradient(135deg, #0284c7 0%, #06b6d4 100%)"
                color="#0284c7"
                loading={metricsQuery.isLoading}
                suffix="units"
              />
              <StatCard
                title="Revenue"
                value={stats?.totalRevenue ?? 0}
                icon="bx-money"
                trend={formatTrend(trends?.revenue)}
                gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                color="#10b981"
                loading={metricsQuery.isLoading}
                formatAsCurrency
              />
              <StatCard
                title="Unique products sold"
                value={stats?.totalSoldItemsCount ?? 0}
                icon="bx-package"
                trend={formatTrend(trends?.soldItemsCount)}
                gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                color="#6366f1"
                loading={metricsQuery.isLoading}
              />
              <StatCard
                title="Purchase expense"
                value={stats?.totalPurchaseExpense ?? 0}
                icon="bx-credit-card"
                trend={formatTrend(trends?.purchaseExpense)}
                gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                color="#ef4444"
                loading={metricsQuery.isLoading}
                formatAsCurrency
              />
              <StatCard
                title="Items purchased"
                value={stats?.totalItemsPurchased ?? 0}
                icon="bx-cart"
                trend={formatTrend(trends?.purchasedItems)}
                gradient="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
                color="#0ea5e9"
                loading={metricsQuery.isLoading}
                suffix="units"
              />
              <StatCard
                title="Stock alerts"
                value={
                  (stats?.deadItems ?? 0) + (stats?.belowShelfLevel ?? 0) + (stats?.nearExpiry ?? 0)
                }
                icon="bx-error"
                gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                color="#f59e0b"
                loading={metricsQuery.isLoading}
                onClick={() => navigateToInventoryWithFilters()}
              />
            </motion.section>
          </Can>

          <div className="charts-grid">
            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_SALES_UPTIME, PERMISSIONS.VIEW_DASHBOARD]}>
              <ChartCard
                title="Sales activity (peak hours)"
                subtitle="When you sell the most in the selected period"
                loading={uptimeQuery.isLoading}
                onRefresh={() => uptimeQuery.refetch()}
                gridColumn="span 4"
                className="chart-fullwidth"
              >
                {!uptimeQuery.isLoading && (
                  <SalesUptimeChart
                    data={uptimeData}
                    bucket={uptimeQuery.data?.bucket}
                    height={300}
                  />
                )}
              </ChartCard>
            </Can>

            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_CHARTS, PERMISSIONS.VIEW_DASHBOARD]}>
              <ChartCard
                title="Revenue trend"
                subtitle="Daily revenue and invoice count"
                loading={chartsQuery.isLoading}
                onRefresh={() => chartsQuery.refetch()}
                gridColumn="span 2"
              >
                {!chartsQuery.isLoading && <RevenueChart data={revenueChartData} height={280} />}
              </ChartCard>

              <ChartCard
                title="Top selling items"
                subtitle="By quantity in selected period"
                loading={chartsQuery.isLoading}
                onRefresh={() => chartsQuery.refetch()}
                gridColumn="span 2"
              >
                {!chartsQuery.isLoading && (
                  <BarChart data={topItemsData} horizontal height={280} title="Qty sold" />
                )}
              </ChartCard>
            </Can>
          </div>

          <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_INVENTORY, PERMISSIONS.VIEW_DASHBOARD]}>
            <section className="insights-section">
              <h2 className="section-title">Inventory insights</h2>
              <div className="insights-grid">
                {inventoryQuery.isLoading && (
                  <p className="text-muted px-2">Loading inventory insights…</p>
                )}
                {!inventoryQuery.isLoading &&
                  (inventoryQuery.data ?? []).map((insight) => {
                    const cfg = insightConfig[insight.category] ?? {
                      icon: 'bx-info-circle',
                      severity: 'info' as const,
                      title: insight.category,
                    };
                    return (
                      <InventoryInsightCard
                        key={insight.category}
                        title={cfg.title}
                        count={insight.count}
                        subtitle={`${insight.items?.length ?? 0} items listed`}
                        icon={cfg.icon}
                        severity={cfg.severity}
                        onClick={() => navigateToInventoryWithFilters(insight.category)}
                      />
                    );
                  })}
              </div>
            </section>
          </Can>

          <div className="recent-tables-grid">
            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_RECENT_SALES, PERMISSIONS.VIEW_DASHBOARD]}>
              <DashboardWidget
                title="Recent sell invoices"
                loading={recentSellsQuery.isLoading}
                error={recentSellsQuery.isError}
                isEmpty={!recentSellsQuery.isLoading && (recentSellsQuery.data?.length ?? 0) === 0}
                onRefresh={() => recentSellsQuery.refetch()}
                seeMoreHref={paths.sells}
              >
                <Table responsive hover size="sm" className="dashboard-recent-table mb-0">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Amount</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentSellsQuery.data ?? []).map((row) => (
                      <tr
                        key={row.id}
                        className="dashboard-recent-row-clickable"
                        onClick={() =>
                          navigate(
                            SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL.replace(
                              ':shopId',
                              shopId,
                            ).replace(':invoiceId', row.id),
                          )
                        }
                      >
                        <td>#{row.invoiceNumber}</td>
                        <td>{formatPkr(row.totalAmount)}</td>
                        <td>{fmtDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DashboardWidget>
            </Can>

            <Can
              anyOf={[
                PERMISSIONS.DASHBOARD_WIDGET_RECENT_SELL_ADJUSTMENTS,
                PERMISSIONS.VIEW_DASHBOARD,
              ]}
            >
              <DashboardWidget
                title="Recent sell adjustments"
                loading={recentAdjQuery.isLoading}
                error={recentAdjQuery.isError}
                isEmpty={!recentAdjQuery.isLoading && (recentAdjQuery.data?.length ?? 0) === 0}
                onRefresh={() => recentAdjQuery.refetch()}
                seeMoreHref={paths.sellAdj}
              >
                <Table responsive hover size="sm" className="dashboard-recent-table mb-0">
                  <thead>
                    <tr>
                      <th>Adjustment</th>
                      <th>Invoice</th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentAdjQuery.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td>{row.adjustmentNumber}</td>
                        <td>{row.originalInvoiceNumber ?? '—'}</td>
                        <td className={row.netAmountDelta >= 0 ? 'text-success' : 'text-danger'}>
                          {formatPkr(row.netAmountDelta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DashboardWidget>
            </Can>

            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_RECENT_STOCK, PERMISSIONS.VIEW_DASHBOARD]}>
              <DashboardWidget
                title="Recent stock movements"
                loading={recentStockQuery.isLoading}
                error={recentStockQuery.isError}
                isEmpty={!recentStockQuery.isLoading && (recentStockQuery.data?.length ?? 0) === 0}
                onRefresh={() => recentStockQuery.refetch()}
                seeMoreHref={paths.stock}
              >
                <Table responsive hover size="sm" className="dashboard-recent-table mb-0">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentStockQuery.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td>{row.itemName}</td>
                        <td>{formatCount(row.quantity)}</td>
                        <td>{row.movementType}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DashboardWidget>
            </Can>

            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_RECENT_LEDGERS, PERMISSIONS.VIEW_DASHBOARD]}>
              <DashboardWidget
                title="Recent ledger entries"
                loading={recentLedgersQuery.isLoading}
                error={recentLedgersQuery.isError}
                isEmpty={
                  !recentLedgersQuery.isLoading && (recentLedgersQuery.data?.length ?? 0) === 0
                }
                onRefresh={() => recentLedgersQuery.refetch()}
                seeMoreHref={paths.ledger}
              >
                <Table responsive hover size="sm" className="dashboard-recent-table mb-0">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recentLedgersQuery.data ?? []).map((row) => (
                      <tr key={row.id}>
                        <td>{row.accountType}</td>
                        <td>{formatPkr(row.debit)}</td>
                        <td>{formatPkr(row.credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </DashboardWidget>
            </Can>

            {/* for the following, give grid column span 4 */}
            <Can anyOf={[PERMISSIONS.DASHBOARD_WIDGET_ACTIVITIES, PERMISSIONS.VIEW_DASHBOARD]}>
              <DashboardWidget
                title="Recent activity"
                subtitle="Latest sales and shop events"
                loading={activitiesQuery.isLoading}
                error={activitiesQuery.isError}
                isEmpty={!activitiesQuery.isLoading && feedActivities.length === 0}
                onRefresh={() => activitiesQuery.refetch()}
                gridColumn="span 2"
              >
                <ActivityFeed activities={feedActivities} maxItems={15} />
              </DashboardWidget>
            </Can>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SellDashboardPage;
